import React, {useCallback, useState} from 'react';
import {CheckCheckIcon} from '../components/icons';
import AuthShell from '../components/auth/AuthShell';
import SmartCaptcha, {isCaptchaEnabled} from '../components/auth/SmartCaptcha';
import {AuthApiError, requestPasswordReset} from '../service/RegistrationService';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

/**
 * Запрос ссылки для смены пароля
 * (`docs/features/2026-08-20_auth-registration-recovery`, FR-24/FR-27).
 *
 * Экран успеха одинаков для существующего и несуществующего адреса — по нему нельзя
 * перебирать аккаунты (SEC-1).
 */
const ForgotPasswordPage: React.FC = () => {
    useDocumentTitle();
    const [email, setEmail] = useState('');
    const [captchaToken, setCaptchaToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const captchaRequired = isCaptchaEnabled();
    const handleCaptchaToken = useCallback((token: string) => setCaptchaToken(token), []);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await requestPasswordReset(email, captchaToken);
            setSubmitted(true);
        } catch (err) {
            setError(err instanceof AuthApiError ? err.description : 'Не удалось отправить письмо. Попробуйте позже.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            heading={<>Забыли пароль?<br/><span className="text-brand-600 dark:text-brand-400">Восстановим доступ</span></>}
            lead="Пришлём ссылку для смены пароля на вашу почту. Если у аккаунта включена двухфакторная аутентификация, понадобится ещё и код из приложения."
        >
            <div data-testid="forgot-password-page">
                {submitted ? (
                    <div className="mt-6 text-center" data-testid="forgot-success">
                        <span className="mx-auto w-12 h-12 rounded-full bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center">
                            <CheckCheckIcon className="w-6 h-6 text-brand-600 dark:text-brand-400"/>
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-ink-900 dark:text-ink-50">Проверьте почту</h2>
                        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">
                            Если аккаунт с таким адресом существует и подтверждён, мы отправили на него ссылку
                            для смены пароля. Ссылка одноразовая и действует час.
                        </p>
                        <a href="/login" className="btn-secondary h-11 mt-6 w-full">Вернуться ко входу</a>
                    </div>
                ) : (
                    <>
                        <h2 className="mt-6 text-2xl font-bold text-center text-ink-900 dark:text-ink-50">
                            Восстановление пароля
                        </h2>
                        <p className="mt-2 text-sm text-center text-ink-500 dark:text-ink-400">
                            Укажите адрес, на который зарегистрирован аккаунт.
                        </p>

                        {error && (
                            <div
                                role="alert"
                                className="mt-6 p-3.5 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30"
                            >
                                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                            </div>
                        )}

                        <form onSubmit={onSubmit} className="mt-6 space-y-5">
                            <div>
                                <label htmlFor="email"
                                       className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                    Электронная почта
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-field h-12"
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    required
                                    autoFocus
                                />
                            </div>

                            <SmartCaptcha onToken={handleCaptchaToken}/>

                            <button
                                type="submit"
                                disabled={loading || (captchaRequired && !captchaToken)}
                                className="btn-accent w-full h-12 text-[15px]"
                            >
                                {loading ? 'Отправляем...' : 'Прислать ссылку'}
                            </button>

                            <a href="/login" className="btn-secondary w-full h-12 text-[15px]">
                                Вспомнил пароль
                            </a>
                        </form>
                    </>
                )}
            </div>
        </AuthShell>
    );
};

export default ForgotPasswordPage;
